import {auth} from '@common/auth/use-auth';
import {FileTypeIcon} from '@common/uploads/components/file-type-icon/file-type-icon';
import {useFileUploadStore} from '@common/uploads/uploader/file-upload-provider';
import {useWorkspaceStore} from '@common/workspace/workspace-store';
import {Button} from '@shadcn/button/button';
import {Tooltip} from '@shadcn/tooltip/tooltip';
import {message} from '@ui/i18n/message';
import {MessageDescriptor} from '@ui/i18n/message-descriptor';
import {useTrans} from '@ui/i18n/use-trans';
import {MixedText} from '@ui/i18n/mixed-text';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {cn} from '@ui/utils/cn';
import {prettyBytes} from '@ui/utils/files/pretty-bytes';
import {
  UploadedFile,
  UploadedFileFromEntry,
} from '@ui/utils/files/uploaded-file';
import {shallowEqual} from '@ui/utils/shallow-equal';
import clsx from 'clsx';
import {AnimatePresence} from 'framer-motion';
import {
  CheckCircleIcon,
  CircleAlertIcon,
  FileUpIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  XIcon,
} from 'lucide-react';
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  memo,
  ReactElement,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {ConfirmationDialog} from '@ui/overlays/dialog/confirmation-dialog';
import {openDialog} from '@ui/overlays/store/dialog-store';
import {toast} from '@ui/toast/toast';
import {useShallow} from 'zustand/react/shallow';
import {
  shortDurationMessage,
  timeLeftMessage,
} from '@common/uploads/uploader/eta/upload-eta';
import {
  useRowEta,
  useSpeedFormatter,
} from '@common/uploads/uploader/eta/use-upload-eta';

interface Props {
  file: UploadedFile | UploadedFileFromEntry;
  className?: string;
  style?: CSSProperties;
}
export const UploadQueueItem = memo(({file, className, style}: Props) => {
  return (
    <div
      className={clsx('flex min-w-0 items-center gap-3', className)}
      style={style}
      data-testid="upload-queue-item"
    >
      <div className="shrink-0 rounded-card-xs border p-2">
        <FileTypeIcon className="size-5.5" mime={file.mime} />
      </div>
      <div className="min-w-0 flex-auto">
        <div
          className="mb-0.5 truncate font-medium"
          title={file.name}
          dir="auto"
        >
          {file.name}
        </div>
        <SizeInfo file={file} />
      </div>
      <FileStatus file={file} />
    </div>
  );
}, shallowEqual);

interface SizeInfoProps {
  file: UploadedFile | UploadedFileFromEntry;
}
function SizeInfo({file}: SizeInfoProps) {
  const {uploading: uploadingSettings} = useSettings();
  const fileUpload = useFileUploadStore(
    useShallow(s => s.fileUploads.get(file.id)),
  );
  const bytesUploaded = fileUpload?.bytesUploaded || 0;
  const backendName = uploadingSettings?.backends?.find(
    backend => backend.id === fileUpload?.options.backendId,
  )?.name;

  const totalBytes = useMemo(() => prettyBytes(file.size), [file]);
  const uploadedBytes = useMemo(
    () => prettyBytes(bytesUploaded),
    [bytesUploaded],
  );

  const workspaceId = fileUpload?.options.metadata?.workspaceId;
  const workspace = useWorkspaceStore
    .getState()
    .workspaces.find(item => item.id === workspaceId);
  const usesOwnerStorage = Boolean(
    workspace &&
      !workspace.is_personal &&
      workspace.owner_id &&
      workspace.owner_id !== auth.user?.id,
  );

  const status = fileUpload?.status;
  const uploading =
    status === 'inProgress' && (fileUpload?.percentage || 0) < 100;
  const eta = useRowEta(
    file.id,
    Math.max(0, file.size - bytesUploaded),
    uploading,
  );
  const formatSpeed = useSpeedFormatter();
  const {trans} = useTrans();
  const lineRef = useRef<HTMLDivElement>(null);
  useFitStatusLine(lineRef, uploading);
  let title: string | undefined;

  let statusMessage: ReactElement;
  if (status === 'completed') {
    const took =
      fileUpload?.activeMs && fileUpload.activeMs > 0
        ? shortDurationMessage(fileUpload.activeMs / 1000, true)
        : null;
    statusMessage = (
      <span data-testid="upload-done">
        {took ? (
          <Trans
            message="Done · :time"
            values={{time: <Trans {...took} />}}
          />
        ) : (
          <Trans message="Done" />
        )}
        {usesOwnerStorage ? (
          <>
            <span className="px-1">·</span>
            <Trans message="Owner's storage" />
          </>
        ) : null}
      </span>
    );
  } else if (status === 'interrupted') {
    statusMessage = (
      <Trans message="Interrupted · Select the file again to resume" />
    );
  } else if (status === 'aborted') {
    statusMessage =
      bytesUploaded > 0 ? (
        <Trans
          message="Paused · :bytesUploaded of :totalBytes"
          values={{
            bytesUploaded: <bdi dir="ltr">{uploadedBytes}</bdi>,
            totalBytes: <bdi dir="ltr">{totalBytes}</bdi>,
          }}
        />
      ) : (
        <Trans message="Paused" />
      );
  } else if (status === 'failed') {
    // Show the actual reason (e.g. too large); a network failure has none.
    const reason = fileUpload?.errorMessage;
    statusMessage = reason ? (
      typeof reason === 'string' ? (
        <span title={reason}>{reason}</span>
      ) : (
        <Trans {...reason} />
      )
    ) : (
      <Trans message="Failed · Retry to try again" />
    );
  } else if (status === 'pending') {
    statusMessage = <bdi dir="ltr">{totalBytes}</bdi>;
  } else if ((fileUpload?.percentage || 0) >= 100) {
    statusMessage = <Trans message="Saving to storage…" />;
  } else {
    const bytesMessage = message(':bytesUploaded of :totalBytes', {
      values: {bytesUploaded: uploadedBytes, totalBytes},
    });
    const etaMessage =
      !eta || eta.estimating
        ? message('Estimating…')
        : eta.seconds != null
          ? message(timeLeftMessage(eta.seconds).message, {
              values: timeLeftMessage(eta.seconds).values,
            })
          : message('Waiting for the network…');
    const speedText = eta?.speed != null ? formatSpeed(eta.speed) : null;
    title = [trans(bytesMessage), speedText, trans(etaMessage)]
      .filter(Boolean)
      .join(' · ');
    // Wide: "8 MB of 60 MB · 12.4 MB/s · About 3 min left". When it does not
    // fit, useFitStatusLine drops the backend name, then the speed, then the
    // sizes, so the time left always stays visible.
    statusMessage = (
      <>
        <span className="group-data-[compact=3]/status:hidden" data-testid="upload-bytes">
          <Trans
            message=":bytesUploaded of :totalBytes"
            values={{
              // Keep "8 MB" in one visual run inside Arabic text.
              bytesUploaded: <bdi dir="ltr">{uploadedBytes}</bdi>,
              totalBytes: <bdi dir="ltr">{totalBytes}</bdi>,
            }}
          />
          <span className="px-1">·</span>
        </span>
        {speedText ? (
          <span
            className="group-data-[compact=2]/status:hidden group-data-[compact=3]/status:hidden"
            data-testid="upload-speed"
          >
            <bdi dir="ltr" className="tabular-nums">
              {speedText}
            </bdi>
            <span className="px-1">·</span>
          </span>
        ) : null}
        <span
          data-testid="upload-eta"
          data-eta-seconds={
            eta && !eta.estimating && eta.seconds != null
              ? Math.round(eta.seconds)
              : undefined
          }
        >
          <Trans {...etaMessage} />
        </span>
      </>
    );
  }

  return (
    <div
      ref={lineRef}
      className={cn(
        'group/status truncate text-xs',
        uploading && 'tabular-nums',
        fileUpload?.status === 'failed'
          ? 'text-destructive'
          : fileUpload?.status === 'aborted' ||
              fileUpload?.status === 'interrupted'
            ? 'text-warning'
            : 'text-muted-foreground',
      )}
      data-testid="upload-status-line"
      title={title}
    >
      {statusMessage}
      {backendName ? (
        <span
          className={cn(
            'text-muted-foreground',
            uploading && 'group-data-[compact]/status:hidden',
          )}
        >
          <span className="px-1">·</span>
          {backendName}
        </span>
      ) : null}
    </div>
  );
}

interface FileStatusProps {
  file: UploadedFile | UploadedFileFromEntry;
}
function FileStatus({file}: FileStatusProps) {
  const fileUpload = useFileUploadStore(
    useShallow(s => s.fileUploads.get(file.id)),
  );
  const pauseUpload = useFileUploadStore(s => s.pauseUpload);
  const resumeUpload = useFileUploadStore(s => s.resumeUpload);
  const cancelUpload = useFileUploadStore(s => s.cancelUpload);
  const reattachFiles = useFileUploadStore(s => s.reattachFiles);
  const inputRef = useRef<HTMLInputElement>(null);
  const percentage = fileUpload?.percentage || 0;
  const status = fileUpload?.status;
  const errorMessage = fileUpload?.errorMessage;

  const {trans} = useTrans();
  const rowEta = useRowEta(
    file.id,
    Math.max(0, file.size - (fileUpload?.bytesUploaded || 0)),
    status === 'inProgress' && percentage < 100,
  );

  const iconButton = (
    label: MessageDescriptor,
    icon: ReactElement,
    onClick: () => void,
    testId: string,
  ) => (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={trans(label)}
            data-testid={testId}
            onClick={onClick}
          />
        }
      >
        {icon}
      </Tooltip.Trigger>
      <Tooltip.Content>
        <Trans {...label} />
      </Tooltip.Content>
    </Tooltip>
  );

  const confirmCancel = async () => {
    const confirmed = await openDialog(CancelUploadDialog, {
      count: 1,
      name: file.name,
      interrupted: status === 'interrupted',
    });
    if (confirmed) {
      await cancelUpload(file.id);
    }
  };

  const cancelButton = iconButton(
    status === 'interrupted' ? message('Discard upload') : message('Cancel upload'),
    <XIcon className="size-4" />,
    () => void confirmCancel(),
    'upload-cancel',
  );

  const pauseButton = iconButton(
    message('Pause upload'),
    <PauseIcon className="size-4" />,
    () => pauseUpload(file.id, true),
    'upload-pause',
  );

  const resumeButton = iconButton(
    message('Resume upload'),
    <PlayIcon className="size-4" />,
    () => resumeUpload(file.id),
    'upload-resume',
  );

  const retryButton = iconButton(
    message('Retry upload'),
    <RotateCcwIcon className="size-4" />,
    () => resumeUpload(file.id),
    'upload-retry',
  );

  const reselectButton = (
    <>
      {iconButton(
        message('Select the file again to resume'),
        <FileUpIcon className="size-4" />,
        () => inputRef.current?.click(),
        'upload-reselect',
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid="upload-reselect-input"
        onChange={e => {
          const files = e.target.files ? [...e.target.files] : [];
          e.target.value = '';
          if (!files.length) return;
          const {matched} = reattachFiles(files, file.id);
          if (!matched) {
            toast.danger(
              message(
                'That is not the same file. Choose “:name” (:size, unchanged since the upload started).',
                {values: {name: file.name, size: prettyBytes(file.size)}},
              ),
            );
          }
        }}
      />
    </>
  );

  const progressButton = (
    <ProgressCircle
      aria-label={trans(message('Upload progress'))}
      valueText={progressValueText(
        trans,
        percentage,
        rowEta && !rowEta.estimating ? rowEta.seconds : null,
        !rowEta || rowEta.estimating,
      )}
      size="size-6"
      value={percentage}
      isIndeterminate={percentage === 100}
      trackWidth={3}
    />
  );

  let statusButton: ReactElement;
  if (status === 'interrupted') {
    statusButton = (
      <AnimatedStatus>
        {reselectButton}
        {cancelButton}
      </AnimatedStatus>
    );
  } else if (status === 'aborted') {
    statusButton = (
      <AnimatedStatus>
        {resumeButton}
        {cancelButton}
      </AnimatedStatus>
    );
  } else if (status === 'failed') {
    const errMessage =
      errorMessage || message('This file could not be uploaded');
    statusButton = (
      <AnimatedStatus>
        {retryButton}
        <Tooltip>
          <Tooltip.Trigger
            render={
              <span
                className="flex size-7 shrink-0 items-center justify-center"
                tabIndex={0}
                data-testid="upload-status-icon"
              />
            }
          >
            <CircleAlertIcon className="size-4.5 text-destructive" />
          </Tooltip.Trigger>
          <Tooltip.Content>
            <MixedText value={errMessage} />
          </Tooltip.Content>
        </Tooltip>
        {cancelButton}
      </AnimatedStatus>
    );
  } else if (status === 'completed') {
    statusButton = (
      <AnimatedStatus>
        <span className="flex size-7 items-center justify-center">
          <CheckCircleIcon
            className="size-4.5 text-positive"
            aria-label={trans(message('Upload complete'))}
          />
        </span>
      </AnimatedStatus>
    );
  } else if (status === 'pending') {
    statusButton = (
      <AnimatedStatus>
        {pauseButton}
        {cancelButton}
      </AnimatedStatus>
    );
  } else {
    statusButton = (
      <AnimatedStatus>
        {progressButton}
        {percentage < 100 ? pauseButton : null}
        {cancelButton}
      </AnimatedStatus>
    );
  }

  return <AnimatePresence>{statusButton}</AnimatePresence>;
}

interface CancelUploadDialogProps {
  count: number;
  name?: string;
  interrupted?: boolean;
}
/** Asks before a cancel deletes the partial upload on the server. */
export function CancelUploadDialog({
  count,
  name,
  interrupted,
}: CancelUploadDialogProps) {
  return (
    <ConfirmationDialog
      isDanger
      title={
        interrupted ? (
          <Trans message="Discard upload?" />
        ) : (
          <Trans
            message="[one Cancel upload?|other Cancel :count uploads?]"
            values={{count}}
          />
        )
      }
      body={
        count === 1 && name ? (
          <Trans
            message="“:name” stops uploading and the part already sent is deleted from the server. This cannot be resumed."
            values={{name}}
          />
        ) : (
          <Trans
            message="[one The unfinished upload stops and the part already sent is deleted from the server.|other :count unfinished uploads stop and the parts already sent are deleted from the server.]"
            values={{count}}
          />
        )
      }
      close={<Trans message="Keep uploading" />}
      confirm={
        interrupted ? (
          <Trans message="Discard" />
        ) : (
          <Trans message="Cancel upload" />
        )
      }
    />
  );
}

interface AnimatedStatusProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag'
> {
  children: ReactNode;
}
function AnimatedStatus({
  children,
  className,
  ...domProps
}: AnimatedStatusProps) {
  return (
    <div
      className={cn(
        // Sized by its content: icon buttons never overlap or clip the name.
        'flex shrink-0 animate-in items-center gap-1 duration-300 fade-in-0',
        className,
      )}
      {...domProps}
    >
      {children}
    </div>
  );
}

/** Screen-reader value for a progress bar: "43% · About 3 min left". */
export function progressValueText(
  trans: (m: MessageDescriptor) => string,
  percentage: number,
  secondsLeft: number | null,
  estimating: boolean,
): string {
  const pct = `${Math.floor(percentage)}%`;
  if (estimating) return `${pct} · ${trans(message('Estimating…'))}`;
  if (secondsLeft == null) return pct;
  const left = timeLeftMessage(secondsLeft);
  return `${pct} · ${trans(message(left.message, {values: left.values}))}`;
}

/**
 * Keeps the uploading status line on one row without clipping: after every
 * render (speed/ETA change once a second) and on resize, raise
 * data-compact 1 → 3 until it fits (1 hides the backend name, 2 the speed,
 * 3 the sizes; the time left always stays).
 */
function useFitStatusLine(ref: RefObject<HTMLDivElement | null>, active: boolean) {
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    if (!active) {
      delete el.dataset.compact;
      return;
    }
    delete el.dataset.compact;
    let level = 0;
    while (el.scrollWidth > el.clientWidth + 1 && level < 3) {
      level++;
      el.dataset.compact = String(level);
    }
  };
  useLayoutEffect(fit);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !active || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => fit());
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
